import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import LoadingState from '../../components/ui/LoadingState';
import Icon from '../../components/AppIcon';
import { coachingConceptsService } from '../../services/coachingConceptsService';
import { conceptsPracticeService } from '../../services/conceptsPracticeService';
import { useAuth } from '../../contexts/AuthContext';

const ConceptsPracticePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  const [concept, setConcept] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Practice state (non-persistent for now)
  const [understood, setUnderstood] = useState(false);
  const [notes, setNotes] = useState('');
  const [savedLocal, setSavedLocal] = useState(false);

  useEffect(() => {
    const loadConcept = async () => {
      try {
        setIsLoading(true);
        setError('');
        const data = await coachingConceptsService.getById(id);
        if (!data) {
          setError('Concept not found');
          return;
        }
        setConcept(data);
      } catch (err) {
        console.error('Error loading concept:', err);
        setError('Failed to load concept');
      } finally {
        setIsLoading(false);
      }
    };

    if (!user) {
      navigate('/authentication');
      return;
    }
    loadConcept();
    window.scrollTo(0, 0);
  }, [id, user, navigate]);

  const handleMarkRep = async () => {
    // Try to persist to Supabase; on error, fallback to localStorage
    try {
      await conceptsPracticeService.logPractice(user?.id, id, understood, notes);
      setSavedLocal(false);
    } catch (err) {
      console.warn('Supabase save failed, falling back to local', err);
      try {
        const key = `concept_practice_${user?.id}`;
        const raw = localStorage.getItem(key);
        const map = raw ? JSON.parse(raw) : {};
        map[id] = { understood, notes, completedAt: new Date().toISOString() };
        localStorage.setItem(key, JSON.stringify(map));
        setSavedLocal(true);
      } catch (lsErr) {
        console.warn('Local save failed', lsErr);
      }
    }
  };

  if (isLoading) {
    return <LoadingState message="Loading concept..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavigationBar />
        <div className="max-w-4xl mx-auto p-6">
          <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg flex items-start gap-3">
            <Icon name="AlertCircle" className="w-6 h-6 text-red-600" />
            <div>
              <h3 className="font-semibold text-red-900">Error</h3>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <NavigationBar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24">
        {/* Inactivity warning banner */}
        {profile?.inactivityWarning && (
          <div className="mb-6 p-4 border-2 border-yellow-200 bg-yellow-50 rounded-lg flex items-start gap-3">
            <Icon name="AlertTriangle" className="w-6 h-6 text-yellow-600" />
            <div>
              <h3 className="font-semibold text-yellow-900">Stay Active</h3>
              <p className="text-sm text-yellow-800">
                Run a drill today to avoid losing <span className="font-semibold">{profile.inactivityWarning.xpAtRisk} XP</span>
                {profile.inactivityWarning.daysUntilDecay === 0 ? ' today' : ` in ${profile.inactivityWarning.daysUntilDecay} day(s)`}.
                Next decay date: <span className="font-mono">{profile.inactivityWarning.nextDecayDate}</span>.
              </p>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary-100 rounded-xl">
              <Icon name="BookOpen" className="w-8 h-8 text-primary-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Concept: {concept?.title}</h1>
              <p className="text-gray-600">Foundation for selling fundamentals. Study, then mark a rep for comprehension.</p>
            </div>
          </div>
        </div>

        {/* Concept content */}
        <div className="bg-white p-6 rounded-xl border-2 border-gray-200 mb-6">
          <p className="text-gray-800 mb-4">{concept?.summary}</p>

          {concept?.bulletPoints && concept?.bulletPoints.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Key Points</h3>
              <ul className="list-disc pl-6 space-y-1 text-gray-700">
                {concept.bulletPoints.map((bp, idx) => (
                  <li key={idx}>{bp}</li>
                ))}
              </ul>
            </div>
          )}

          {concept?.exampleScript && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Example</h4>
              <p className="text-sm text-gray-900 italic">"{concept.exampleScript}"</p>
            </div>
          )}
        </div>

        {/* Practice panel */}
        <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
          <h3 className="text-lg font-bold text-gray-900 mb-2">Comprehension Check</h3>
          <p className="text-sm text-gray-600 mb-4">Mark whether you understand and can explain this concept, and jot what to improve.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Understood</label>
              <div className="flex items-center gap-2">
                <button className={`px-3 py-2 rounded-lg border ${understood ? 'bg-green-600 text-white border-green-700' : 'bg-gray-100 text-gray-700 border-gray-200'}`} onClick={() => setUnderstood(true)}>Yes</button>
                <button className={`px-3 py-2 rounded-lg border ${!understood ? 'bg-red-600 text-white border-red-700' : 'bg-gray-100 text-gray-700 border-gray-200'}`} onClick={() => setUnderstood(false)}>No</button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={5} className="w-full border rounded-lg p-2" placeholder="What needs clarity or practice?" />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={handleMarkRep} className="py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg">Mark Rep</button>
            {savedLocal ? (
              <span className="text-sm text-green-700">Saved locally. We’ll sync these later.</span>
            ) : (
              <span className="text-sm text-green-700">Saved!</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConceptsPracticePage;
